/* eslint-disable camelcase */

import { ISelectInputOption } from '@rocketmakers/armstrong';
import React = require('react');
import { useParams } from 'react-router-dom';
import {
  DeviceType,
  RuleActionType,
  HeatingControlHeatPump,
  RoomDialWrf06,
  RoomSensorMastertherm,
  PhysicalLocationID,
  RoomSensorWrf06,
  DeviceInfo,
  RuleCategory,
  HeatingSystem,
  HeatingZoneID,
  HeatPumpHeatingControl,
} from '../../../../shared/api-clients/_api';
import { useRoutePaths } from '../../../../shared/context/paths';
import { useBeeConfig } from '../../hooks/beeConfig';
import { routePaths } from '../../routes';
import { getDeviceWithZoneDescription, makeIdForDevice } from './utils';

const heatPumpHeatingControlMapping = Object.values(HeatPumpHeatingControl).reduce((acc, curr, i) => {
  acc[curr] = `Heat pump zone ${i + 1}`;
  return acc;
}, {});

const masterthermThermostats = Object.values(HeatPumpHeatingControl).map(control => ({
  deviceType: DeviceType.MASTERTHERM,
  heat_pump_heating_control: control,
}));

export const useConfigureHeatPumpHeatingZone = getZoneDescription => {
  const { heatingZone, heatPumpType } = useParams<{
    heatingZone: HeatingZoneID;
    heatPumpType: string;
  }>();
  const { replace } = useRoutePaths();

  const {
    createDevice,
    editDevice,
    devices,
    getDevicesByType,
    getDevicesByZone,
    removeDevice,
    getAssignedResourcesFor,
  } = useBeeConfig();

  const currentDevices = React.useMemo(() => {
    const hzDevices = heatingZone ? getDevicesByZone(heatingZone) : [];

    const heatPump = hzDevices.find(
      ({ deviceType, params: { action_type } }) =>
        deviceType === DeviceType.MASTERTHERM && action_type === RuleActionType.CONTROL
    ) as HeatingControlHeatPump;

    if (!heatPump) return;

    const tempDial = hzDevices.find(
      ({ deviceType, params: { action_type } }) =>
        deviceType === DeviceType.WRF06 && action_type === RuleActionType.CONTROL
    ) as RoomDialWrf06;

    const tempDialMonitoring = hzDevices.find(
      ({ deviceType, params: { action_type } }) =>
        deviceType === DeviceType.WRF06 && action_type === RuleActionType.MONITORING
    ) as RoomSensorMastertherm;

    const tempSensor = (hzDevices.find(
      ({ deviceType, params: { action_type } }) =>
        deviceType === DeviceType.MASTERTHERM && action_type === RuleActionType.MONITORING
    ) || tempDial) as RoomSensorMastertherm;

    return {
      heatPump,
      tempDial,
      tempDialMonitoring,
      tempSensor,
    };
  }, [heatingZone, devices]);

  const {
    temperatureDialOptions,
    temperatureSensorOptions,
    assignedZones,
    assignedHeatPumps,
    assignedDials,
    assignedSensors,
    assignedLocations,
  } = React.useMemo(() => {
    const heatPumps = getDevicesByType<HeatingControlHeatPump>([DeviceType.MASTERTHERM])?.filter(
      d => d.params.action_type === RuleActionType.CONTROL
    );
    const tempDials = getDevicesByType<RoomDialWrf06>([DeviceType.WRF06])?.filter(
      d => d.params.action_type === RuleActionType.CONTROL
    );
    const tempSensors = getDevicesByType<RoomSensorMastertherm>([DeviceType.MASTERTHERM])?.filter(
      d => d.params.action_type === RuleActionType.MONITORING
    );

    return {
      temperatureDialOptions: tempDials.map<ISelectInputOption>(temperatureDial => ({
        id: makeIdForDevice(temperatureDial),
        name: getDeviceWithZoneDescription(temperatureDial, getZoneDescription),
        data: temperatureDial,
      })),
      temperatureSensorOptions: masterthermThermostats.map<ISelectInputOption>(temperatureSensor => ({
        id: temperatureSensor.heat_pump_heating_control,
        name: heatPumpHeatingControlMapping[temperatureSensor.heat_pump_heating_control],
        data: temperatureSensor,
      })),
      assignedZones: Array.from(
        new Set([...heatPumps, ...tempDials, ...tempSensors].map(({ params }) => params.zone_id))
      ),
      assignedHeatPumps: heatPumps
        .filter(tempSensor => tempSensor.params.zone_id)
        .map(({ params }) => params.heat_pump_heating_control.toString()),
      assignedDials: tempDials.filter(tempDial => tempDial.params.zone_id).map(makeIdForDevice),
      assignedSensors: tempSensors
        .filter(tempSensor => tempSensor.params.zone_id)
        .map(t => t.params.heat_pump_heating_control),
      assignedLocations: getAssignedResourcesFor(
        [DeviceType.MASTERTHERM],
        'install_location',
        currentDevices?.tempSensor?.params.install_location
      ),
    };
  }, [devices]);

  const findWrf06TempSensorInLocation = (tempDialLocation: PhysicalLocationID) => {
    const wrf06TempSensors = getDevicesByType<RoomSensorWrf06>([DeviceType.WRF06]).filter(
      ({ params: { action_type } }) => action_type === RuleActionType.MONITORING
    );

    return wrf06TempSensors.find(({ params: { install_location } }) => install_location === tempDialLocation);
  };

  const handleSave = dataBinder => {
    const zone_id = dataBinder.getValue('heatingZoneID');
    const { data: tempDial }: { data?: RoomDialWrf06 } = temperatureDialOptions.find(
      ({ id }) => id === dataBinder.getValue('temperatureDial')
    );
    const tempDialMonitoring = findWrf06TempSensorInLocation(tempDial.params.install_location);

    const devicesToAdd: DeviceInfo[] = [
      {
        deviceType: DeviceType.MASTERTHERM,
        params: {
          zone_id,
          category: RuleCategory.HEATING,
          heat_pump_heating_control: dataBinder.getValue('control'),
          heating_system: HeatingSystem.MASTERTHERM,
          action_type: RuleActionType.CONTROL,
          address: 0,
        },
      },
      {
        deviceType: DeviceType.MASTERTHERM,
        params: {
          install_location: dataBinder.getValue('temperatureSensorLocation'),
          zone_id,
          category: RuleCategory.HEATING,
          action_type: RuleActionType.MONITORING,
          heat_pump_heating_control: dataBinder.getValue('control'),
          address: 0,
          primary_zone_temperature_sensor: '1',
        },
      },
    ];

    createDevice(...devicesToAdd);

    editDevice(tempDial, {
      ...tempDial,
      params: {
        ...tempDial.params,
        zone_id,
      },
    });

    editDevice(tempDialMonitoring, {
      ...tempDialMonitoring,
      params: {
        ...tempDialMonitoring.params,
        zone_id,
      },
    });

    replace(routePaths.heatingSystem.assignHeatPumpHeatingZones.root, { heatPumpType });
  };

  const removeFromZone = <T extends DeviceInfo>(device: T) => {
    editDevice(device, {
      ...device,
      params: { ...device.params, zone_id: null },
    });
  };

  const handleEdit = dataBinder => {
    const zone_id = dataBinder.getValue('heatingZoneID');
    const { data: tempDial } = temperatureDialOptions.find(({ id }) => id === dataBinder.getValue('temperatureDial'));
    const tempDialMonitoring = findWrf06TempSensorInLocation(tempDial.params.install_location);

    const { tempDial: currentTempDial, tempDialMonitoring: currentTempDialMonitoring } = currentDevices;

    // Update dial
    if (currentTempDial !== tempDial) {
      removeFromZone(currentTempDial);
      removeFromZone(currentTempDialMonitoring);
    }
    editDevice(tempDial, { ...tempDial, params: { ...tempDial.params, zone_id } });
    editDevice(tempDialMonitoring, {
      ...tempDialMonitoring,
      params: { ...tempDialMonitoring.params, zone_id },
    });

    // Update sensor
    editDevice(currentDevices.tempSensor, {
      deviceType: DeviceType.MASTERTHERM,
      params: {
        install_location: dataBinder.getValue('temperatureSensorLocation'),
        zone_id,
        category: RuleCategory.HEATING,
        action_type: RuleActionType.MONITORING,
        heat_pump_heating_control: dataBinder.getValue('control'),
        address: 0,
        primary_zone_temperature_sensor: '1',
      },
    });

    // Update heat pump
    editDevice(currentDevices.heatPump, {
      deviceType: DeviceType.MASTERTHERM,
      params: {
        zone_id,
        category: RuleCategory.HEATING,
        heat_pump_heating_control: dataBinder.getValue('control'),
        heating_system: HeatingSystem.MASTERTHERM,
        action_type: RuleActionType.CONTROL,
        address: 0,
      },
    });

    replace(routePaths.heatingSystem.assignHeatPumpHeatingZones.root, { heatPumpType });
  };

  const handleDelete = React.useCallback(() => {
    const { tempDial, tempDialMonitoring, tempSensor, heatPump } = currentDevices;

    removeDevice(heatPump);
    removeDevice(tempSensor);
    if (tempDial) {
      removeFromZone(tempDial);
      removeFromZone(tempDialMonitoring);
    }

    replace(routePaths.heatingSystem.assignHeatPumpHeatingZones.root, { heatPumpType });
  }, [currentDevices]);

  return {
    handleSave,
    handleEdit,
    handleDelete,
    currentDevices,
    temperatureDialOptions,
    temperatureSensorOptions,
    assignedZones,
    assignedHeatPumps,
    assignedDials,
    assignedSensors,
    assignedLocations,
  };
};
